import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useGetTransactionHistoryQuery } from "../../store/auth/authApi";
import { Pagination } from "../../components";
import { formatMoney } from "../../utils";

// Column widths for skeleton bars (match relative column content)
const SKELETON_WIDTHS = ["w-24", "w-36", "w-20", "w-16", "w-14", "w-20", "w-12"];

function TableSkeleton() {
  const rows = 10;
  return (
    <table className="min-w-full divide-y divide-gray-200 border-collapse">
      <thead className="bg-gray-50">
        <tr>
          {SKELETON_WIDTHS.map((_, i) => (
            <th key={i} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              <div className={`h-3 bg-gray-200 rounded animate-pulse ${SKELETON_WIDTHS[i]}`} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex} className="bg-white">
            {SKELETON_WIDTHS.map((widthClass, colIndex) => (
              <td
                key={colIndex}
                className={`px-4 py-3 border-0 ${colIndex >= 4 ? "text-right" : ""}`}
              >
                <div
                  className={`h-4 bg-gray-100 rounded animate-pulse ${widthClass} inline-block`}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TransactionHistoryEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-5">
        <svg
          className="w-10 h-10 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">No transaction history yet</h3>
      <p className="text-gray-500 text-sm text-center max-w-sm">
        When cooperatives are created, or when they add member slots or SMS units, those transactions will appear here.
      </p>
    </div>
  );
}

const PRODUCT_LABELS = {
  member_slots: "Member slots",
  sms_units: "SMS units",
};

const EVENT_LABELS = {
  initial: "Initial",
  top_up: "Top-up",
  purchase: "Purchase",
};

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

export default function TransactionHistoryPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [productType, setProductType] = useState("");
  const [eventType, setEventType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = {
    page: currentPage,
    per_page: 20,
    ...(productType && { product_type: productType }),
    ...(eventType && { event_type: eventType }),
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
  };

  const {
    data: response,
    isLoading,
    error,
  } = useGetTransactionHistoryQuery(params);

  const list = response?.data?.data ?? response?.data ?? [];
  const items = Array.isArray(list) ? list : [];
  const totalPages = response?.lastPage ?? response?.meta?.last_page ?? 1;
  const hasMorePages = response?.hasMorePages ?? false;
  const hasPrevPage = currentPage > 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setProductType("");
    setEventType("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <p className="text-red-600">
            {error?.data?.message || error?.message || "Failed to load transaction history."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
        <h1 className="text-2xl font-bold text-[#45464e] mb-4">
          Transaction history
        </h1>
        <p className="text-gray-500 text-sm mb-4">
          View all org purchases: member slots (initial and top-up), SMS units, and other products.
        </p>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            >
              <option value="">All</option>
              <option value="member_slots">Member slots</option>
              <option value="sms_units">SMS units</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            >
              <option value="">All</option>
              <option value="initial">Initial</option>
              <option value="top_up">Top-up</option>
              <option value="purchase">Purchase</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date from</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date to</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-[#8B313A] focus:border-[#8B313A]"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleApplyFilters}
              className="bg-[#8B313A] hover:bg-[#6B252A] text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          {isLoading ? (
            <TableSkeleton />
          ) : items.length === 0 ? (
            <TransactionHistoryEmptyState />
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cooperative
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount charged
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.company_id ? (
                        <Link
                          to={`/cooperatives/${row.company_id}`}
                          className="text-[#8B313A] hover:underline font-medium"
                        >
                          {row.company_name || `Company #${row.company_id}`}
                        </Link>
                      ) : (
                        row.company_name || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {PRODUCT_LABELS[row.product_type] ?? row.product_type}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {EVENT_LABELS[row.event_type] ?? row.event_type}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.quantity != null ? Number(row.quantity).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.amount_charged != null
                        ? formatMoney(Number(row.amount_charged))
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.discount_percentage != null
                        ? `${Number(row.discount_percentage)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {items.length > 0 && (
          <div className="mt-4 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              hasNextPage={hasMorePages}
              hasPrevPage={hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
